import React from 'react';

interface ConfigProps {
  stockLevel: number;
  batchSize: number;
  requestsPerSecond: number;
  handlingTimeOmd: number;
  updateFlagValue: number;
  // Conditional Rules
  enableMinimumPriceCheck?: boolean;
  minimumPriceThreshold?: number;
  minimumPriceMessage?: string;
  enableMinimumStockCheck?: boolean;
  minimumStockThreshold?: number;
  minimumStockMessage?: string;
  priceMarkupPercentage?: number; // Phase 2.1 - Markup % para cálculo de preço
}

interface WpsConfigProps {
  config: ConfigProps;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const WpsConfig: React.FC<WpsConfigProps> = ({ config, onChange }) => {
  return (
    <div className="space-y-4">
      {/* Stock Level */}
      <div>
        <label htmlFor="stockLevel" className="block text-sm font-medium text-gray-700 mb-1">
          Nível de Estoque
        </label>
        <input
          id="stockLevel"
          type="number"
          name="stockLevel"
          value={Number(config.stockLevel ?? 0)}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          min="0"
        />
      </div>

      {/* Batch Size */}
      <div>
        <label htmlFor="batchSize" className="block text-sm font-medium text-gray-700 mb-1">
          Tamanho do Lote
        </label>
        <input
          id="batchSize"
          type="number"
          name="batchSize"
          value={Number(config.batchSize ?? 0)}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          min="1"
          max="1000"
        />
        <p className="text-xs text-gray-500 mt-1">Número de produtos processados por vez (recomendado: 100)</p>
      </div>

      {/* Requests Per Second */}
      <div>
        <label htmlFor="requestsPerSecond" className="block text-sm font-medium text-gray-700 mb-1">
          Requisições por Segundo
        </label>
        <input
          id="requestsPerSecond"
          type="number"
          name="requestsPerSecond"
          value={Number(config.requestsPerSecond ?? 5)}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          min="1"
          max="20"
        />
      </div>

      {/* Handling Time OMD */}
      <div>
        <label htmlFor="handlingTimeOmd" className="block text-sm font-medium text-gray-700 mb-1">
          Tempo de Manuseio OMD (dias)
        </label>
        <input
          id="handlingTimeOmd"
          type="number"
          name="handlingTimeOmd"
          value={Number(config.handlingTimeOmd ?? 0)}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          min="0"
          max="30"
        />
      </div>

      {/* Update Flag Value */}
      <div>
        <label htmlFor="updateFlagValue" className="block text-sm font-medium text-gray-700 mb-1">
          Código de Atualização (ID da Loja)
        </label>
        <input
          id="updateFlagValue"
          type="number"
          name="updateFlagValue"
          value={Number(config.updateFlagValue ?? 0)}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          min="1"
          max="99"
        />
        <p className="text-xs text-gray-500 mt-1">
          Este código será usado para marcar produtos atualizados por esta loja. Use um valor único para cada loja.
        </p>
      </div>

      {/* Conditional Rules */}
      <div className="mt-6 space-y-4">
        <h3 className="text-lg font-semibold">Conditional Rules</h3>

        {/* Minimum Price Check */}
        <div className="border rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium">Minimum Price Check</h4>
            <input
              id="enableMinimumPriceCheck"
              type="checkbox"
              name="enableMinimumPriceCheck"
              checked={Boolean(config.enableMinimumPriceCheck)}
              onChange={onChange}
              className="h-4 w-4"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="minimumPriceThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Price Threshold ($)
              </label>
              <input
                id="minimumPriceThreshold"
                type="number"
                name="minimumPriceThreshold"
                value={Number(config.minimumPriceThreshold ?? 0)}
                onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label htmlFor="minimumPriceMessage" className="block text-sm font-medium text-gray-700 mb-1">
                Custom Message for Price Rule
              </label>
              <input
                id="minimumPriceMessage"
                type="text"
                name="minimumPriceMessage"
                value={config.minimumPriceMessage || ''}
                onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Minimum Stock Check */}
        <div className="border rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium">Minimum Stock Check</h4>
            <input
              id="enableMinimumStockCheck"
              type="checkbox"
              name="enableMinimumStockCheck"
              checked={Boolean(config.enableMinimumStockCheck)}
              onChange={onChange}
              className="h-4 w-4"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="minimumStockThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Stock Threshold (units)
              </label>
              <input
                id="minimumStockThreshold"
                type="number"
                name="minimumStockThreshold"
                value={Number(config.minimumStockThreshold ?? 0)}
                onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="1"
              />
            </div>
            <div>
              <label htmlFor="minimumStockMessage" className="block text-sm font-medium text-gray-700 mb-1">
                Custom Message for Stock Rule
              </label>
              <input
                id="minimumStockMessage"
                type="text"
                name="minimumStockMessage"
                value={config.minimumStockMessage || ''}
                onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Seção: Phase 2.1 - Price Markup */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-purple-900 mb-3">
          💰 Phase 2.1 - Price Markup
        </h3>
        
        <div>
          <label htmlFor="priceMarkupPercentage" className="block text-sm font-medium text-gray-700 mb-1">
            Markup Percentage (%)
          </label>
          <input
            id="priceMarkupPercentage"
            type="number"
            name="priceMarkupPercentage"
            min="0"
            step="0.1"
            value={config.priceMarkupPercentage || 15}
            onChange={onChange}
            className="input"
          />
          <p className="text-sm text-gray-500 mt-1">
            Percentual de markup aplicado ao calcular o preço Amazon.
          </p>
          <p className="text-sm text-purple-700 mt-2">
            <strong>ℹ️ Fórmula:</strong> Amazon Price = (supplier_price + shipping) × (1 + markup% / 100)
          </p>
          <p className="text-sm text-purple-700 mt-1">
            <strong>📝 Exemplo:</strong> Produto $10 + Shipping $5 com 15% markup = $17.25
          </p>
        </div>
      </div>
    </div>
  );
};

export default WpsConfig;
